import * as encoding from "lib0/encoding";
import * as decoding from "lib0/decoding";
import * as syncProtocol from "y-protocols/sync";
import * as awarenessProtocol from "y-protocols/awareness";
import { Observable } from "lib0/observable";

import {
  clearWebSocketStatus,
  handleWebSocketClose,
  handleWebSocketTicketError,
} from "../../../auth/websocketLifecycle";

const MESSAGE_SYNC = 0;
const MESSAGE_AWARENESS = 1;

// Close codes that must NOT trigger reconnect (spec §11).
const FATAL_CODES = [4001, 4002, 4003, 4006, 4030, 4031, 4040];

export class CollabProvider extends Observable {
  // getUrl returns a fresh single-use WebSocket ticket URL for every connection.
  // onControl: (msg) => void — export_request / error / pong / fatal.
  constructor(baseUrl, doc, { getUrl, onControl, awareness } = {}) {
    super();
    this.baseUrl = baseUrl;
    this.doc = doc;
    this.awareness = awareness || new awarenessProtocol.Awareness(doc);
    this.getUrl = getUrl;
    this.onControl = onControl;
    this.ws = null;
    this.synced = false;
    this.shouldConnect = true;
    this.retries = 0;
    this._heartbeat = null;
    this._reconnect = null;
    this._connecting = false;

    this._onUpdate = (update, origin) => {
      if (origin === this) return; // don't echo remote updates back
      const enc = encoding.createEncoder();
      encoding.writeVarUint(enc, MESSAGE_SYNC);
      syncProtocol.writeUpdate(enc, update);
      this._sendBinary(encoding.toUint8Array(enc));
    };
    this.doc.on("update", this._onUpdate);

    this._onAwareness = ({ added, updated, removed }) => {
      const changed = added.concat(updated, removed);
      const enc = encoding.createEncoder();
      encoding.writeVarUint(enc, MESSAGE_AWARENESS);
      encoding.writeVarUint8Array(
        enc,
        awarenessProtocol.encodeAwarenessUpdate(this.awareness, changed)
      );
      this._sendBinary(encoding.toUint8Array(enc));
    };
    this.awareness.on("update", this._onAwareness);

    this.connect();
  }

  async connect() {
    if (this.ws || this._connecting || !this.shouldConnect) return;
    this._connecting = true;
    let url;
    try {
      url = await this.getUrl();
    } catch (error) {
      this._connecting = false;
      if (!handleWebSocketTicketError(error, this.baseUrl)) {
        this.onControl?.({
          type: "fatal",
          code: error?.response?.status === 404 ? 4404 : 4401,
        });
        return;
      }
      if (this.shouldConnect) {
        this._reconnect = setTimeout(() => this.connect(), 1000);
      }
      return;
    }
    const ws = new WebSocket(url);
    this._connecting = false;
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    this.synced = false;

    ws.onopen = () => {
      this.retries = 0;
      clearWebSocketStatus(this.baseUrl);
      const enc = encoding.createEncoder();
      encoding.writeVarUint(enc, MESSAGE_SYNC);
      syncProtocol.writeSyncStep1(enc, this.doc);
      this._sendBinary(encoding.toUint8Array(enc));
      if (this.awareness.getLocalState()) {
        const a = encoding.createEncoder();
        encoding.writeVarUint(a, MESSAGE_AWARENESS);
        encoding.writeVarUint8Array(
          a,
          awarenessProtocol.encodeAwarenessUpdate(this.awareness, [this.doc.clientID])
        );
        this._sendBinary(encoding.toUint8Array(a));
      }
      this._startHeartbeat();
      this.emit("status", [{ status: "connected" }]);
    };

    ws.onmessage = (event) => {
      if (typeof event.data === "string") {
        let msg;
        try {
          msg = JSON.parse(event.data);
        } catch {
          return;
        }
        this.onControl?.(msg);
        return;
      }
      const dec = decoding.createDecoder(new Uint8Array(event.data));
      const type = decoding.readVarUint(dec);
      if (type === MESSAGE_SYNC) {
        const enc = encoding.createEncoder();
        encoding.writeVarUint(enc, MESSAGE_SYNC);
        const syncType = syncProtocol.readSyncMessage(dec, enc, this.doc, this);
        if (syncType === syncProtocol.messageYjsSyncStep2 && !this.synced) {
          this.synced = true;
          this.emit("synced", [true]);
        }
        if (encoding.length(enc) > 1) this._sendBinary(encoding.toUint8Array(enc));
      } else if (type === MESSAGE_AWARENESS) {
        awarenessProtocol.applyAwarenessUpdate(
          this.awareness,
          decoding.readVarUint8Array(dec),
          this
        );
      }
    };

    ws.onclose = (event) => {
      this._stopHeartbeat();
      this.ws = null;
      this.synced = false;
      const remotes = Array.from(this.awareness.getStates().keys()).filter(
        (id) => id !== this.doc.clientID
      );
      awarenessProtocol.removeAwarenessStates(this.awareness, remotes, this);
      this.emit("status", [{ status: "disconnected", code: event.code }]);

      if (!handleWebSocketClose(event, this.baseUrl)) {
        this.onControl?.({ type: "fatal", code: event.code });
        return;
      }
      if (FATAL_CODES.includes(event.code)) {
        this.onControl?.({ type: "fatal", code: event.code });
        return;
      }
      if (!this.shouldConnect) return;
      const delay = Math.min(1000 * 2 ** this.retries, 15000);
      this.retries += 1;
      this._reconnect = setTimeout(() => this.connect(), delay);
    };
  }

  _sendBinary(bytes) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(bytes);
  }
  sendControl(obj) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
  }
  _startHeartbeat() {
    this._stopHeartbeat();
    this._heartbeat = setInterval(() => this.sendControl({ type: "ping" }), 30000);
  }
  _stopHeartbeat() {
    if (this._heartbeat) clearInterval(this._heartbeat);
    this._heartbeat = null;
  }

  destroy() {
    this.shouldConnect = false;
    if (this._reconnect) clearTimeout(this._reconnect);
    this._stopHeartbeat();
    this.doc.off("update", this._onUpdate);
    this.awareness.off("update", this._onAwareness);
    awarenessProtocol.removeAwarenessStates(this.awareness, [this.doc.clientID], this);
    if (this.ws) {
      this.ws.onmessage = null; // no inbound frame can reach ySyncPlugin now
      this.ws.onclose = null; // don't schedule a reconnect on this close
      this.ws.onopen = null;
      try {
        this.ws.close();
      } catch {
        /* noop */
      }
    }
    this.ws = null;
    super.destroy();
  }
}
